import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";
import { 
  BarChart3, 
  Calendar,
  MapPin,
  Home,
  Clock,
  TrendingUp,
  DollarSign,
  Users,
  RefreshCw,
  Filter
} from "lucide-react";

export default function AdminPaymentReports() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    // Set default date range to current year
    const now = new Date();
    const yearStart = `${now.getFullYear()}-01-01`;
    const today = now.toISOString().split("T")[0];
    setStartDate(yearStart);
    setEndDate(today);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchReport();
    }
  }, [startDate, endDate]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/payments/summary?start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReportData(response.data);
    } catch (error) {
      toast.error("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "₦0";
    return `₦${Number(amount).toLocaleString()}`;
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return "";
    try {
      const [year, month] = monthStr.split("-");
      const date = new Date(year, parseInt(month) - 1);
      return date.toLocaleDateString('en-NG', { month: 'short', year: 'numeric' });
    } catch {
      return monthStr;
    }
  };

  const getPercentage = (amount, total) => {
    if (!total || total === 0) return 0;
    return ((amount / total) * 100).toFixed(1);
  };

  // Quick date range presets
  const setPreset = (preset) => {
    const now = new Date();
    let start, end;
    
    switch (preset) {
      case "thisMonth":
        start = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        end = now.toISOString().split("T")[0];
        break;
      case "lastMonth":
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        start = lastMonth.toISOString().split("T")[0];
        end = lastMonthEnd.toISOString().split("T")[0];
        break;
      case "thisQuarter":
        const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        start = quarterStart.toISOString().split("T")[0];
        end = now.toISOString().split("T")[0];
        break;
      case "thisYear":
        start = `${now.getFullYear()}-01-01`;
        end = now.toISOString().split("T")[0];
        break;
      case "lastYear":
        start = `${now.getFullYear() - 1}-01-01`;
        end = `${now.getFullYear() - 1}-12-31`;
        break;
      case "allTime":
        start = "2020-01-01";
        end = now.toISOString().split("T")[0];
        break;
      default:
        return;
    }
    
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-payment-reports">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-primary flex items-center gap-3">
              <BarChart3 className="w-8 h-8" />
              Payment Reports
            </h1>
            <p className="text-text-muted mt-1">Analyze payment trends and earnings by property</p>
          </div>

          {/* Date Range Filter */}
          <Card className="border-stone-200 mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1 grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start-date">Start Date</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="h-11"
                      data-testid="start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-date">End Date</Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="h-11"
                      data-testid="end-date-input"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPreset("thisMonth")}>
                    This Month
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPreset("lastMonth")}>
                    Last Month
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPreset("thisQuarter")}>
                    This Quarter
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPreset("thisYear")}>
                    This Year
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPreset("allTime")}>
                    All Time
                  </Button>
                </div>
                <Button
                  onClick={fetchReport}
                  disabled={loading}
                  className="bg-primary hover:bg-primary-800"
                  data-testid="apply-filter-btn"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Apply
                </Button>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
              <p className="text-text-muted">Loading report data...</p>
            </div>
          ) : reportData ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-stone-200 bg-gradient-to-br from-green-50 to-green-100/50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-text-muted">Total Earnings</p>
                        <p className="text-3xl font-bold text-green-600 mt-1">
                          {formatCurrency(reportData.summary.total_amount)}
                        </p>
                      </div>
                      <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center">
                        <DollarSign className="w-7 h-7 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-stone-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-text-muted">Total Payments</p>
                        <p className="text-3xl font-bold text-primary mt-1">
                          {reportData.summary.payment_count}
                        </p>
                      </div>
                      <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                        <TrendingUp className="w-7 h-7 text-primary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-stone-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-text-muted">Average Payment</p>
                        <p className="text-3xl font-bold text-blue-600 mt-1">
                          {formatCurrency(reportData.summary.average_payment)}
                        </p>
                      </div>
                      <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center">
                        <BarChart3 className="w-7 h-7 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Breakdown Tabs */}
              <Tabs defaultValue="location" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="location" data-testid="tab-location">
                    <MapPin className="w-4 h-4 mr-2" />
                    By Location
                  </TabsTrigger>
                  <TabsTrigger value="apartment" data-testid="tab-apartment">
                    <Home className="w-4 h-4 mr-2" />
                    By Apartment
                  </TabsTrigger>
                  <TabsTrigger value="frequency" data-testid="tab-frequency">
                    <Clock className="w-4 h-4 mr-2" />
                    By Frequency
                  </TabsTrigger>
                  <TabsTrigger value="monthly" data-testid="tab-monthly">
                    <Calendar className="w-4 h-4 mr-2" />
                    Monthly
                  </TabsTrigger>
                  <TabsTrigger value="tenants" data-testid="tab-tenants">
                    <Users className="w-4 h-4 mr-2" />
                    Top Tenants
                  </TabsTrigger>
                </TabsList>

                {/* By Location */}
                <TabsContent value="location">
                  <Card className="border-stone-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Earnings by Property Location</CardTitle>
                      <CardDescription>Revenue breakdown by each property location</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {reportData.by_location.length === 0 ? (
                        <p className="text-text-muted text-center py-8">No payment data for this period</p>
                      ) : (
                        <div className="space-y-4">
                          {reportData.by_location.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-4">
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-primary flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    {item.location}
                                  </span>
                                  <span className="font-bold text-green-600">
                                    {formatCurrency(item.amount)}
                                  </span>
                                </div>
                                <div className="w-full bg-stone-100 rounded-full h-3">
                                  <div 
                                    className="bg-primary h-3 rounded-full transition-all"
                                    style={{ width: `${getPercentage(item.amount, reportData.summary.total_amount)}%` }}
                                  />
                                </div>
                                <div className="flex justify-between text-xs text-text-muted mt-1">
                                  <span>{item.count} payment(s)</span>
                                  <span>{getPercentage(item.amount, reportData.summary.total_amount)}%</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* By Apartment */}
                <TabsContent value="apartment">
                  <Card className="border-stone-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Earnings by Apartment</CardTitle>
                      <CardDescription>Revenue breakdown by location and apartment number</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {reportData.by_apartment.length === 0 ? (
                        <p className="text-text-muted text-center py-8">No payment data for this period</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Location</TableHead>
                                <TableHead>Apartment</TableHead>
                                <TableHead className="text-right">Payments</TableHead>
                                <TableHead className="text-right">Total Amount</TableHead>
                                <TableHead className="text-right">% of Total</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {reportData.by_apartment.map((item, idx) => (
                                <TableRow key={idx}>
                                  <TableCell className="font-medium">
                                    <div className="flex items-center gap-2">
                                      <MapPin className="w-4 h-4 text-primary" />
                                      {item.location}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline">#{item.apartment}</Badge>
                                  </TableCell>
                                  <TableCell className="text-right">{item.count}</TableCell>
                                  <TableCell className="text-right font-bold text-green-600">
                                    {formatCurrency(item.amount)}
                                  </TableCell>
                                  <TableCell className="text-right text-text-muted">
                                    {getPercentage(item.amount, reportData.summary.total_amount)}%
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* By Frequency */}
                <TabsContent value="frequency">
                  <Card className="border-stone-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Earnings by Payment Frequency</CardTitle>
                      <CardDescription>Revenue breakdown by payment type</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {reportData.by_frequency.length === 0 ? (
                        <p className="text-text-muted text-center py-8">No payment data for this period</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          {reportData.by_frequency.map((item, idx) => (
                            <Card key={idx} className="border-stone-200">
                              <CardContent className="pt-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <Clock className="w-4 h-4 text-primary" />
                                  <span className="font-medium">{item.frequency}</span>
                                </div>
                                <p className="text-2xl font-bold text-green-600">
                                  {formatCurrency(item.amount)}
                                </p>
                                <p className="text-sm text-text-muted">
                                  {item.count} payment(s) • {getPercentage(item.amount, reportData.summary.total_amount)}%
                                </p>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Monthly Breakdown */}
                <TabsContent value="monthly">
                  <Card className="border-stone-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Monthly Earnings</CardTitle>
                      <CardDescription>Revenue trend over time</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {reportData.by_month.length === 0 ? (
                        <p className="text-text-muted text-center py-8">No payment data for this period</p>
                      ) : (
                        <div className="space-y-3">
                          {reportData.by_month.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-4 p-3 bg-stone-50 rounded-xl">
                              <div className="w-24 font-medium text-primary">
                                {formatMonth(item.month)}
                              </div>
                              <div className="flex-1">
                                <div className="w-full bg-stone-200 rounded-full h-4">
                                  <div 
                                    className="bg-gradient-to-r from-primary to-primary-700 h-4 rounded-full transition-all"
                                    style={{ 
                                      width: `${Math.max(5, getPercentage(item.amount, Math.max(...reportData.by_month.map(m => m.amount))))}%` 
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="text-right w-32">
                                <p className="font-bold text-green-600">{formatCurrency(item.amount)}</p>
                                <p className="text-xs text-text-muted">{item.count} payment(s)</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Top Tenants */}
                <TabsContent value="tenants">
                  <Card className="border-stone-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Top Paying Tenants</CardTitle>
                      <CardDescription>Tenants with the highest payment amounts</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {reportData.top_tenants.length === 0 ? (
                        <p className="text-text-muted text-center py-8">No payment data for this period</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Rank</TableHead>
                                <TableHead>Tenant Name</TableHead>
                                <TableHead>Location</TableHead>
                                <TableHead className="text-right">Payments</TableHead>
                                <TableHead className="text-right">Total Amount</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {reportData.top_tenants.map((tenant, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>
                                    <Badge className={
                                      idx === 0 ? "bg-yellow-100 text-yellow-700" :
                                      idx === 1 ? "bg-stone-200 text-stone-700" :
                                      idx === 2 ? "bg-amber-100 text-amber-700" :
                                      "bg-stone-100 text-stone-600"
                                    }>
                                      #{idx + 1}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="font-medium">{tenant.tenant_name}</TableCell>
                                  <TableCell>
                                    <span className="text-text-muted">
                                      {tenant.location} - Apt #{tenant.apartment}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-right">{tenant.count}</TableCell>
                                  <TableCell className="text-right font-bold text-green-600">
                                    {formatCurrency(tenant.amount)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <Card className="border-stone-200">
              <CardContent className="py-12 text-center">
                <BarChart3 className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-text-muted">Select a date range to view reports</p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
